import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, EnvelopeSimple, Star, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/api/client';
import {
  addEmail,
  listEmails,
  removeEmail,
  setPrimaryEmail,
  verifyEmail,
  type UserEmail,
} from '@/api/account';

export const ACCOUNT_EMAILS_QUERY_KEY = ['account', 'emails'] as const;

// EmailAddresses manages every address tied to the signed-in account. This is
// account-level (org-less), so the same card serves the admin SPA's
// Settings → Profile and the portal's /me.
//
// Why it matters beyond contact details: a club that entered an OLD or
// ALTERNATIVE address on someone's member row only resolves to this account
// once that address is listed here AND verified. Unverified rows match nothing.
export function EmailAddresses() {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Dev-only verification tokens returned by add, keyed by email id, so a
  // pending row can be verified in one click without email delivery wired.
  const [devTokens, setDevTokens] = useState<Record<string, string>>({});

  const emails = useQuery({
    queryKey: ACCOUNT_EMAILS_QUERY_KEY,
    queryFn: () => listEmails(token),
    enabled: auth.isAuthenticated,
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey: ACCOUNT_EMAILS_QUERY_KEY });

  const add = useMutation({
    mutationFn: () => addEmail(token, newEmail.trim()),
    onSuccess: (res) => {
      setErrorMsg(null);
      setNewEmail('');
      if (res.verify_token) setDevTokens((m) => ({ ...m, [res.id]: res.verify_token! }));
      void refetch();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : '';
      setErrorMsg(
        code === 'email_taken'
          ? t('account.emails.taken')
          : code === 'invalid_email'
            ? t('account.emails.invalid')
            : t('account.emails.error'),
      );
    },
  });

  const rows = emails.data ?? [];

  return (
    <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
        >
          <EnvelopeSimple size={20} weight="bold" />
        </span>
        <div className="flex flex-col gap-1">
          <h2 className="font-heading text-lg font-bold">{t('account.emails.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('account.emails.body')}</p>
        </div>
      </div>

      {rows.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {rows.map((e) => (
            <EmailRow key={e.id} email={e} devToken={devTokens[e.id]} onChanged={refetch} />
          ))}
        </ul>
      ) : null}

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (newEmail.trim()) add.mutate();
        }}
      >
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          className="min-w-0 flex-1"
          placeholder={t('account.emails.addPlaceholder')}
          value={newEmail}
          onChange={(ev) => setNewEmail(ev.target.value)}
          aria-label={t('account.emails.addLabel')}
        />
        <Button type="submit" disabled={add.isPending || !newEmail.trim()}>
          {t('account.emails.addCta')}
        </Button>
      </form>
      {errorMsg ? (
        <p className="text-xs text-destructive" role="alert">
          {errorMsg}
        </p>
      ) : null}
    </div>
  );
}

function EmailRow({
  email,
  devToken,
  onChanged,
}: {
  email: UserEmail;
  devToken: string | undefined;
  onChanged: () => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const confirm = useConfirm();

  const verify = useMutation({
    mutationFn: () => verifyEmail(token, devToken ?? ''),
    onSuccess: onChanged,
  });
  const makePrimary = useMutation({
    mutationFn: () => setPrimaryEmail(token, email.id),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => removeEmail(token, email.id),
    onSuccess: onChanged,
  });

  async function confirmRemove() {
    const ok = await confirm({
      title: t('account.emails.removeConfirmTitle'),
      body: t('account.emails.removeConfirmBody', { email: email.email }),
      confirmLabel: t('account.emails.remove'),
      tone: 'destructive',
    });
    if (ok) remove.mutate();
  }

  return (
    <li className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm">{email.email}</span>

      {email.is_primary ? (
        <span className="inline-flex items-center gap-1 rounded-pill border border-lime bg-lime px-2 py-0.5 text-xs font-semibold text-ink">
          <Star weight="fill" size={12} aria-hidden />
          {t('account.emails.primary')}
        </span>
      ) : email.verified ? (
        <span className="inline-flex items-center gap-1 rounded-pill border border-aqua bg-aqua px-2 py-0.5 text-xs font-semibold text-ink">
          <CheckCircle weight="fill" size={12} aria-hidden />
          {t('account.emails.verified')}
        </span>
      ) : (
        <span className="rounded-pill border border-cream bg-cream px-2 py-0.5 text-xs font-semibold text-ink">
          {t('account.emails.pending')}
        </span>
      )}

      {!email.verified && devToken ? (
        <Button
          size="sm"
          variant="outline"
          disabled={verify.isPending}
          onClick={() => verify.mutate()}
        >
          {t('account.emails.verifyCta')}
        </Button>
      ) : null}
      {email.verified && !email.is_primary ? (
        <Button
          size="sm"
          variant="outline"
          disabled={makePrimary.isPending}
          onClick={() => makePrimary.mutate()}
        >
          {t('account.emails.makePrimary')}
        </Button>
      ) : null}
      {!email.is_primary ? (
        <Button
          size="icon"
          variant="ghost"
          className="text-foreground/50 hover:text-destructive"
          disabled={remove.isPending}
          onClick={() => void confirmRemove()}
          aria-label={t('account.emails.remove')}
        >
          <Trash aria-hidden />
        </Button>
      ) : null}
    </li>
  );
}
